/**
 * MessageItem Component
 * =====================
 * 
 * Single message bubble with reactions, audio player, read receipts.
 * 
 * Handles message type variations:
 * - Text messages
 * - Audio/voice messages  
 * - System messages
 * - Reactions display and picker
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

// Reactions config - should match Chat.js
const REACTIONS = [
  { key: 'heart', emoji: '❤️' },
  { key: 'laugh', emoji: '😂' },
  { key: 'fire', emoji: '🔥' },
  { key: 'wow', emoji: '😮' },
  { key: 'sad', emoji: '😢' },
  { key: 'thumbsup', emoji: '👍' },
];

export function MessageItem({
  message,
  isOwnMessage,
  matchProfile,
  onReactionPickerOpen,
  reactionUI,
  onReactionToggle,
  onReactionPickerClose,
}) {
  const bubbleClasses = isOwnMessage
    ? "bg-rose-500 text-white rounded-2xl rounded-br-sm"
    : "bg-gray-100 text-gray-900 rounded-2xl rounded-bl-sm";
  
  // Check if this message has the reaction picker open
  const isPickerOpen = reactionUI?.openFor === message.id;
  
  // 🎯 PATCH: Defensive normalization - ensure reactions is always an array
  // Backend may return: undefined, null, {}, [], or {emoji: count} object
  const normalizeReactions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    // If it's an object map {emoji: count}, convert to array format
    if (typeof raw === 'object') {
      return Object.entries(raw).map(([emoji, count]) => ({ emoji, count: typeof count === 'number' ? count : 1 }));
    }
    return [];
  };
  
  const reactions = normalizeReactions(message.reactions);
  const reactionCounts = {};
  reactions.forEach(r => {
    if (r && r.emoji) {
      reactionCounts[r.emoji] = (reactionCounts[r.emoji] || 0) + (r.count || 1);
    }
  });
  
  return (
    <div 
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}
      data-testid={`message-item-${message.id}`}
    >
      <div 
        className={`max-w-[80%] sm:max-w-[70%] ${bubbleClasses} px-3 sm:px-4 py-2 relative group`}
        onDoubleClick={(e) => {
          if (onReactionPickerOpen) {
            onReactionPickerOpen(message, e);
          }
        }}
      >
        {/* Message Content */}
        {message.audio_url ? (
          <AudioMessage 
            audioUrl={message.audio_url} 
            durationMs={message.audio_duration_ms}
            isOwnMessage={isOwnMessage}
          />
        ) : (
          <div className="text-sm sm:text-base break-words whitespace-pre-wrap">
            {message.text}
          </div>
        )}
        
        {/* Timestamp and Read Receipt */}
        <div className={`text-[10px] mt-1 flex items-center gap-1 ${
          isOwnMessage ? 'text-rose-200 justify-end' : 'text-gray-400'
        }`}>
          {formatTime(message.created_at)}
          {isOwnMessage && message.read_at && (
            <Check className="w-3 h-3 text-rose-200" />
          )}
        </div>
        
        {/* Reaction Pills */}
        {Object.keys(reactionCounts).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(reactionCounts).map(([emoji, count]) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReactionToggle?.(message.id, emoji)}
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isOwnMessage 
                    ? 'bg-rose-400/50 hover:bg-rose-400' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {emoji} {count > 1 && count}
              </button>
            ))}
          </div>
        )}
        
        {/* Reaction Picker Portal */}
        {isPickerOpen && typeof document !== 'undefined' && createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={onReactionPickerClose}
              onTouchStart={(e) => {
                e.stopPropagation();
                onReactionPickerClose?.();
              }}
            />
            
            {/* Picker */}
            <div
              className="fixed z-[9999]"
              style={{
                top: `${reactionUI?.top || 0}px`,
                left: `${reactionUI?.left || 0}px`,
                width: "min(340px, calc(100vw - 16px))",
                touchAction: 'none',
                overscrollBehavior: 'contain',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center gap-1 rounded-full px-2 py-1.5 shadow-xl border overflow-x-auto ${
                  isOwnMessage
                    ? "bg-slate-900 border-slate-700"
                    : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                }`}
                style={{ touchAction: 'pan-x' }}
              >
                {REACTIONS.map((reaction) => {
                  const hasReacted = reactions.some(
                    r => r.emoji === reaction.emoji && r.user_id === message.currentUserId
                  );
                  return (
                    <button
                      key={reaction.key}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReactionToggle?.(message.id, reaction.emoji);
                        onReactionPickerClose?.();
                      }}
                      className={`reaction-emoji text-[22px] leading-none hover:scale-110 transition-transform rounded-full px-1 flex-shrink-0 ${
                        hasReacted ? "ring-2 ring-emerald-500" : ""
                      }`}
                      title={hasReacted ? `Remove ${reaction.emoji}` : `React with ${reaction.emoji}`}
                    >
                      {reaction.emoji}
                    </button>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}
      </div>
    </div>
  );
}

/**
 * AudioMessage - Voice message player
 */
function AudioMessage({ audioUrl, durationMs, isOwnMessage }) {
  const duration = durationMs ? Math.round(durationMs / 1000) : 0;
  
  return (
    <div className="flex items-center gap-2">
      <audio 
        src={audioUrl} 
        controls 
        className="h-8 w-full max-w-[200px]"
        style={{ 
          filter: isOwnMessage ? 'invert(1) hue-rotate(180deg)' : 'none' 
        }}
      />
      {duration > 0 && (
        <span className={`text-xs ${isOwnMessage ? 'text-rose-200' : 'text-gray-400'}`}>
          {duration}s
        </span>
      )}
    </div>
  );
}

/**
 * Format timestamp for display
 */
function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default MessageItem;
